import { useState, useEffect } from 'react';
import {
  Datagrid,
  NumberField,
  FunctionField,
  Pagination,
  useNotify,
  ListContextProvider,
} from 'react-admin';
import {
  Box,
  Typography,
  Card,
  TextField as MuiTextField,
  InputAdornment,
  Avatar,
} from '@mui/material';
import { Search, HatGlasses } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { snakeCase } from '@musedrops/shared';
import { supabase } from '../../../services/supabase';
import { PageToolbar } from '../../../shared/components/PageToolbar';
import { RelativeTimeField } from '../../../shared/components/RelativeTimeField';
import { CopyableId } from '../../../shared/components/CopyableId';

interface UserRecord {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string;
  storiesPlayed: number | null;
  sessions: number | null;
  lastSeen: string;
  isAnonymous: boolean;
}

// Auth details returned by the get-users edge function (both the ids
// enrichment mode and the email/name search mode share this shape)
interface AuthUserDetails {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string;
  isAnonymous?: boolean;
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The get-users edge function accepts at most 50 user IDs per request, so the
// page size must never exceed 50.
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sort, setSort] = useState({
    field: 'lastSeen',
    order: 'DESC' as 'DESC' | 'ASC',
  });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const notify = useNotify();

  // Debounce the search box: non-UUID terms hit the get-users edge function
  // (which scans auth users server-side), so don't fire on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    // Guard against out-of-order responses when page/sort/search change quickly
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        setLoading(true);

        // user_profiles has one row per active user (created on first stream
        // contact), so it's the canonical paginated user source. Profiles
        // carry no email/name, so a UUID term filters directly while any
        // other term is resolved to user IDs via the get-users search mode.
        const searchTerm = search.trim();

        // When searching by email/name, the search response already carries
        // auth details — reuse it as the page's enrichment source.
        let searchedAuthUsers: AuthUserDetails[] | null = null;

        let query = supabase
          .from('user_profiles')
          .select('user_id, stories_played, sessions, last_seen, is_anonymous', {
            count: 'exact',
          });

        if (searchTerm) {
          if (UUID_REGEX.test(searchTerm)) {
            query = query.eq('user_id', searchTerm);
          } else {
            // Email/name search: get-users returns up to 50 matching auth
            // users (case-insensitive substring on email + display name)
            const { data: searchData, error: searchError } =
              await supabase.functions.invoke('get-users', {
                body: { search: searchTerm },
              });
            if (cancelled) return;

            if (searchError || !searchData?.users) {
              notify('Error searching users', { type: 'error' });
              setUsers([]);
              setTotal(0);
              return;
            }

            searchedAuthUsers = searchData.users as AuthUserDetails[];
            if (searchedAuthUsers.length === 0) {
              // No matches is a normal outcome, not an error
              setUsers([]);
              setTotal(0);
              return;
            }

            query = query.in(
              'user_id',
              searchedAuthUsers.map(u => u.id)
            );
          }
        }

        const start = (page - 1) * perPage;
        const {
          data: profiles,
          error: profilesError,
          count,
        } = await query
          .order(snakeCase(sort.field), {
            ascending: sort.order === 'ASC',
            nullsFirst: false,
          })
          // Stable tie-breaker so pagination doesn't skip/duplicate rows when
          // sorting on non-unique columns
          .order('user_id', { ascending: true })
          .range(start, start + perPage - 1);

        if (profilesError) throw profilesError;
        if (cancelled) return;

        setTotal(count ?? 0);

        // Profile-only records, in server sort order
        const profileRecords: UserRecord[] = (profiles ?? []).map(p => ({
          id: p.user_id,
          email: '',
          displayName: '',
          avatarUrl: '',
          storiesPlayed: p.stories_played ?? 0,
          sessions: p.sessions ?? null,
          lastSeen: p.last_seen ?? '',
          isAnonymous: p.is_anonymous ?? true,
        }));

        if (profileRecords.length === 0) {
          setUsers([]);
          return;
        }

        // Enrich the current page with auth details. An email/name search
        // already fetched them; otherwise ask get-users for this page's IDs
        // only (≤ 50, within the get-users cap)
        let authUsers = searchedAuthUsers;
        if (!authUsers) {
          const { data: usersData, error: usersError } =
            await supabase.functions.invoke('get-users', {
              body: { userIds: profileRecords.map(r => r.id) },
            });
          if (cancelled) return;

          if (usersError || !usersData?.users) {
            notify('Error fetching user details', { type: 'error' });
            // Fall back to showing profile data with bare IDs
            setUsers(profileRecords);
            return;
          }

          authUsers = usersData.users as AuthUserDetails[];
        }

        const authMap = new Map<string, AuthUserDetails>(
          authUsers.map(u => [u.id, u])
        );

        setUsers(
          profileRecords.map(record => {
            const auth = authMap.get(record.id);
            return {
              ...record,
              email: auth?.email ?? '',
              displayName: auth?.displayName ?? '',
              avatarUrl: auth?.avatarUrl ?? '',
              isAnonymous: auth?.isAnonymous ?? record.isAnonymous,
            };
          })
        );
      } catch {
        if (!cancelled) {
          notify('Error fetching users', { type: 'error' });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      cancelled = true;
    };
  }, [notify, sort, page, perPage, search]);

  const handleRowClick = (id: string | number) => {
    navigate(`/users/${id}`);
    return false as const;
  };

  if (loading && total === null) {
    return (
      <Box sx={{ p: 3 }}>
        <PageToolbar title="Users" subtitle="Loading users..." />
        <Typography sx={{ p: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageToolbar
        title="Users"
        subtitle={`${total ?? 0} user${total !== 1 ? 's' : ''} with activity`}
      />
      <MuiTextField
        size="small"
        placeholder="Search by email, name, or user ID..."
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        sx={{ mt: 2, mb: 1, width: 320 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Card>
        <ListContextProvider
          value={
            {
              data: users,
              total: total ?? 0,
              isLoading: loading,
              isFetching: loading,
              page,
              perPage,
              setPage,
              setPerPage,
              setFilters: () => {},
              displayedFilters: {},
              filterValues: {},
              showFilter: () => {},
              hideFilter: () => {},
              sort,
              setSort: (newSort: { field: string; order: 'ASC' | 'DESC' }) => {
                setSort(newSort);
                setPage(1);
              },
              resource: 'users',
              refetch: () => {},
              error: null,
              isPending: false,
              selectedIds: [],
              onSelect: () => {},
              onSelectAll: () => {},
              onToggleItem: () => {},
              onUnselectItems: () => {},
            } as any
          }
        >
          <Datagrid
            bulkActionButtons={false}
            rowClick={handleRowClick}
            size="small"
            sx={{
              '& .RaDatagrid-row': {
                cursor: 'pointer',
              },
            }}
          >
            <FunctionField
              label="User"
              render={(record: UserRecord) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {record.isAnonymous ? (
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#1a1a1a' }}>
                      <HatGlasses size={14} color="white" />
                    </Avatar>
                  ) : (
                    <Avatar
                      src={record.avatarUrl || undefined}
                      sx={{ width: 24, height: 24 }}
                    />
                  )}
                  {record.isAnonymous ? (
                    <span style={{ color: '#999' }}>(Anon)</span>
                  ) : (
                    <span style={{ fontWeight: 600 }}>
                      {record.displayName || record.email || '-'}
                    </span>
                  )}
                </Box>
              )}
            />
            <FunctionField
              label="User ID"
              render={(record: UserRecord) => (
                <CopyableId id={record.id} showChars={8} />
              )}
            />
            <NumberField
              source="storiesPlayed"
              label="Stories Played"
              emptyText="-"
            />
            <NumberField
              source="sessions"
              label="Sessions"
              emptyText="-"
            />
            <RelativeTimeField
              source="lastSeen"
              label="Last Seen"
              emptyText="-"
            />
          </Datagrid>
          <Pagination rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} />
        </ListContextProvider>
      </Card>
    </Box>
  );
};
